import React from "react";
import { motion } from "framer-motion";
import { GALLERY_IMAGES } from "@/lib/constants";

export const Gallery = () => {
  return (
    <section
      id="gallery"
      className="relative py-24 sm:py-32 bg-[#0B1726]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#1E88E5]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445]">
                Real jobs. Real crews.
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-6xl font-black uppercase tracking-tight text-white">
              Our work, <br />
              <span className="text-[#1E88E5]">on the clock.</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-md text-lg">
            No stock photos. These are our crews on real jobsites across Colorado
            Springs, the Denver metro & surrounding states — night shifts, fleet
            jobs, downtown calls.
          </p>
        </div>

        {/* Uniform grid — all tiles same aspect */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {GALLERY_IMAGES.map((img, i) => {
            return (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: Math.min(i * 0.05, 0.4) }}
                data-testid={`gallery-item-${i}`}
                className="group relative overflow-hidden rounded-md border border-slate-800 bg-slate-950 aspect-square"
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  loading={i < 4 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1726] via-[#0B1726]/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                {/* Caption */}
                <figcaption className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                  <div className="font-heading text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] mb-1.5">
                    {img.tag}
                  </div>
                  <div className="font-heading font-bold uppercase tracking-tight text-white leading-tight text-base sm:text-lg">
                    {img.caption}
                  </div>
                </figcaption>
                {/* Corner accent on hover */}
                <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#E6B445] opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.figure>
            );
          })}
        </div>

        <div className="mt-12 flex items-center justify-center">
          <a
            href="/quote"
            data-testid="gallery-cta"
            className="group inline-flex items-center gap-3 bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-bold tracking-wider uppercase px-7 py-4 rounded-sm text-sm transition-all"
          >
            Book Your Job
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};
